/**
 * 動的グループの詳細。タブは ルール / メンバー / 権限セット / 履歴。
 *
 * ルールを保存するとその場で再評価される(人数が古いまま残らない)。ただし
 * **保存しただけでは権限は配らない**。実際に本番へ流すのは「初期同期」で、
 * 広すぎる条件を保存した瞬間に何百件も流れるのを避けるため人が明示的に押す。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type {
  GroupEvent,
  GroupMember,
  GroupRuleNode,
  PermissionSet,
  PermissionSetInput,
} from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { GroupRuleEditor } from "./GroupRuleEditor";
import { emptyRule } from "./groupRule";
import {
  PERMISSION_SET_KINDS,
  groupEventColor,
  groupEventLabel,
  statusColor,
  statusLabel,
} from "./constants";
import {
  countNote,
  useGroup,
  useGroupEvents,
  useGroupFields,
  useGroupMembers,
  useInvalidateGroups,
  usePermissionSets,
} from "./groupHooks";
import { errorText, useHrPerms } from "./hooks";

const { Text } = Typography;

function PermissionSetsTab(
  { groupId, canAdmin, groupActive }:
  { groupId: number; canAdmin: boolean; groupActive: boolean },
) {
  const api = useApi();
  const invalidate = useInvalidateGroups();
  const query = usePermissionSets(groupId);
  const [editing, setEditing] = useState<PermissionSet | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<PermissionSetInput>();

  const open = creating || editing !== null;
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing)
      form.setFieldsValue({
        name: editing.name,
        kind: editing.kind,
        target: editing.target,
        note: editing.note,
      });
  }, [open, editing, form]);

  const save = useMutation({
    mutationFn: (body: PermissionSetInput) =>
      editing
        ? api.updatePermissionSet(editing.id, body)
        : api.createPermissionSet(groupId, body),
    onSuccess: (ps) => {
      void message.success(editing ? "更新した" : "追加した");
      if (ps.warning) void message.warning(ps.warning);
      setCreating(false);
      setEditing(null);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.updatePermissionSet(id, { active }),
    onSuccess: () => invalidate(),
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deletePermissionSet(id),
    onSuccess: () => {
      void message.success("消した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const revokeAll = useMutation({
    mutationFn: (id: number) => api.revokeAllPermissionSet(id),
    onSuccess: (r) => {
      // **外部に触らず台帳だけ動かした数**(同じ宛先を別の権限セットも配っている)は
      // 別に出す。0 件のときに「何も起きなかった」と見えるのを避ける
      const moved = r.transferred ? ` / 他の権限セットへ付け替え ${r.transferred} 件` : "";
      // まだ送っていない付与は起票ではなく取り消しで畳む(fix1 レビュー 中 6)
      const undone = r.cancelled ? ` / 未送出の付与を取り消し ${r.cancelled} 件` : "";
      void message.success(`剥奪を ${r.created} 件起票した${moved}${undone}`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<PermissionSet> = [
    { title: "名前", width: 160, dataIndex: "name" },
    { title: "種類", dataIndex: "kind_label", width: 160 },
    { title: "相手", width: 240, dataIndex: "target" },
    {
      title: "流し方",
      width: 280,
      render: (_: unknown, ps: PermissionSet) =>
        ps.configured ? (
          <Tag color="blue">自動({ps.connector})</Tag>
        ) : (
          <Tag>手作業({ps.connector} 未設定)</Tag>
        ),
    },
    {
      title: "配り済み",
      dataIndex: "granted_count",
      width: 90,
      render: (n: number) => (n ? n : <Text type="secondary">0</Text>),
    },
    { title: "待ち", dataIndex: "pending_tasks", width: 90 },
    {
      title: "状態",
      width: 110,
      render: (_: unknown, ps: PermissionSet) =>
        ps.active ? <Tag color="green">稼働中</Tag> : <Tag>止めている</Tag>,
    },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 360,
            render: (_: unknown, ps: PermissionSet) => (
              <Space size={4} wrap>
                <Button size="small" onClick={() => setEditing(ps)}>
                  編集
                </Button>
                {/* 止まっているグループでは動かせないが、**止めるのは許す**
                    (サーバーが断るのは ON にするときだけ)。ここを一律に
                    disabled にすると、止めたグループの権限セットを畳めなくなる */}
                <Switch
                  size="small"
                  checked={ps.active}
                  disabled={!groupActive && !ps.active}
                  loading={toggle.isPending}
                  onChange={(v) => toggle.mutate({ id: ps.id, active: v })}
                />
                <Popconfirm
                  title="配ったものを全部剥がす"
                  description={
                    <>
                      配り済みの {ps.granted_count} 人ぶんの剥奪を起票する。
                      <br />
                      まだ送っていない付与は取り消す(待ちが {ps.pending_tasks} 件)。
                      <br />
                      結果待ちが残っていると断られる(消化してから実行する)。
                      <br />
                      同じ相手を別の権限セットも配っているぶんは剥がさない。
                    </>
                  }
                  okText="剥奪を起票する"
                  cancelText="やめる"
                  disabled={!ps.granted_count && !ps.pending_tasks}
                  onConfirm={() => revokeAll.mutate(ps.id)}
                >
                  {/* 台帳が空でも**未送出の付与**があれば押せる(API が取り消す)。
                      両方 0 のときだけ落とす。送出済みで結果待ちのものが残って
                      いれば API が 409 を返すので、その文をそのまま出す
                      (fix1 レビュー 中 6) */}
                  <Button size="small" danger
                          disabled={!ps.granted_count && !ps.pending_tasks}
                          loading={revokeAll.isPending}>
                    全剥奪
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="この権限セットを消す"
                  description="配り済み・待ちが残っていると消せない(先に全剥奪する)"
                  okText="消す"
                  cancelText="やめる"
                  onConfirm={() => remove.mutate(ps.id)}
                >
                  <Button size="small" danger>
                    削除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }} gap={12} wrap>
        <Text type="secondary" style={{ fontSize: 12 }}>
          グループに入った人へ配るもの。コネクタが設定済みなら自動、未設定なら手順付きの手作業タスクになる。
          「配り済み」は外部に配り終えている人数。畳むときは「全剥奪」で剥がしてから消す。
        </Text>
        {canAdmin && (
          /* 止まっているグループでは作れない(サーバーが 409)。押してから
             断られるより、押せない理由を先に出す */
          <Tooltip title={groupActive ? "" : "先にグループを再開する"}>
            {/* disabled な Button は pointer-events が無く、そのままだと
                Tooltip が出ない。span で包んで hover を受ける */}
            <span
              style={{
                display: "inline-block",
                ...(groupActive ? {} : { cursor: "not-allowed" }),
              }}
            >
              <Button type="primary" disabled={!groupActive} onClick={() => setCreating(true)}>
                権限セットを追加
              </Button>
            </span>
          </Tooltip>
        )}
      </Flex>

      <Table<PermissionSet>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={false}
      />

      <Modal
        open={open}
        title={editing ? `権限セットの編集(${editing.name})` : "権限セットを追加"}
        okText="保存"
        cancelText="やめる"
        confirmLoading={save.isPending}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={() => void form.validateFields().then((v) => save.mutate(v))}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="名前" rules={[{ required: true, message: "名前は必須" }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="kind"
            label="種類"
            initialValue="gws_group"
            rules={[{ required: true }]}
          >
            <Select options={PERMISSION_SET_KINDS} />
          </Form.Item>
          <Form.Item
            name="target"
            label="相手"
            extra="Google グループはメールアドレス、Keycloak はカタログ項目名(app-… のグループ)"
            rules={[{ required: true, message: "相手は必須" }]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="note" label="備考">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export function GroupDetailPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const id = Number(groupId);
  const { canAdmin } = useHrPerms();
  const invalidate = useInvalidateGroups();
  const group = useGroup(Number.isFinite(id) ? id : undefined);
  const fields = useGroupFields();
  const members = useGroupMembers(Number.isFinite(id) ? id : undefined);
  const events = useGroupEvents(Number.isFinite(id) ? id : undefined);
  const [rule, setRule] = useState<GroupRuleNode>(emptyRule());
  const [count, setCount] = useState<number | null>(null);

  const loaded = group.data;
  useEffect(() => {
    if (!loaded) return;
    const r = loaded.rule as GroupRuleNode;
    setRule(r && "conds" in r ? r : emptyRule());
  }, [loaded]);

  const preview = useMutation({
    mutationFn: () => api.previewGroup(rule),
    onSuccess: (r) => setCount(r.count),
    onError: (e) => void message.error(errorText(e)),
  });

  const save = useMutation({
    mutationFn: () => api.updateGroup(id, { rule }),
    onSuccess: () => {
      void message.success("保存した(その場で再評価した)");
      setCount(null);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const toggle = useMutation({
    mutationFn: (active: boolean) => api.updateGroup(id, { active }),
    onSuccess: (g) => {
      void message.success(g.active ? "再開した" : "止めた(メンバーは凍結する)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const reevaluate = useMutation({
    mutationFn: () => api.reevaluateGroup(id),
    onSuccess: (r) => {
      void message.success(
        r.skipped || `再評価した(追加 ${r.joined.length} / 除外 ${r.left.length})`,
      );
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const sync = useMutation({
    mutationFn: () => api.syncGroup(id),
    onSuccess: (r) => {
      void message.success(`起票した(新規 ${r.created} / 既にある ${r.skipped})`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteGroup(id),
    onSuccess: () => {
      void message.success("消した");
      invalidate();
      navigate("/hr/groups");
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (group.isLoading) return <Spin />;
  if (group.isError)
    return (
      <Alert
        type="error"
        showIcon
        message="グループを取得できなかった"
        description={errorText(group.error)}
      />
    );
  if (!group.data) return null;
  const g = group.data;

  const memberColumns: ColumnsType<GroupMember> = [
    { title: "社員番号", dataIndex: "emp_code", width: 120 },
    {
      title: "氏名",
      width: 160,
      dataIndex: "name",
      render: (v: string, m) => <Link to={`/hr/employees/${m.id}`}>{v}</Link>,
    },
    { title: "メール", width: 240, dataIndex: "email" },
    { title: "所属", width: 160, dataIndex: "department" },
    { title: "役職", dataIndex: "position", width: 160 },
    {
      title: "在籍",
      dataIndex: "status",
      width: 110,
      render: (v: string) => <Tag color={statusColor(v)}>{statusLabel(v)}</Tag>,
    },
  ];

  const eventColumns: ColumnsType<GroupEvent> = [
    {
      title: "出入り",
      dataIndex: "kind",
      width: 110,
      render: (v: string) => <Tag color={groupEventColor(v)}>{groupEventLabel(v)}</Tag>,
    },
    {
      title: "対象者",
      width: 160,
      dataIndex: "employee_name",
      render: (v: string, ev) => (ev.employee_id ? <Link to={`/hr/employees/${ev.employee_id}`}>{v}</Link> : v),
    },
    { title: "引き金", width: 200, dataIndex: "trigger" },
    { title: "発効日", dataIndex: "effective_on", width: 120 },
    { title: "記録", width: 180, render: (_: unknown, ev: GroupEvent) => jst(ev.at) },
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }} gap={12} wrap>
        <Space>
          <Link to="/hr/groups">一覧へ</Link>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {g.name}
          </Typography.Title>
          {g.active ? <Tag color="green">稼働中</Tag> : <Tag>止めている</Tag>}
        </Space>
        {canAdmin && (
          <Space wrap>
            <Button loading={reevaluate.isPending} onClick={() => reevaluate.mutate()}>
              いま再評価する
            </Button>
            <Popconfirm
              title="初期同期を流す"
              description="いまのメンバー全員ぶんの付与タスクを起票する。既に待っている人は二重に作らない"
              okText="起票する"
              cancelText="やめる"
              disabled={!g.active}
              onConfirm={() => sync.mutate()}
            >
              {/* 止まっているグループのメンバーは凍結されていて、配ると剥奪が
                  起票されない(サーバーも 409)。押せない理由を先に出す */}
              <Tooltip title={g.active ? "" : "先にグループを再開する"}>
                {/* 同上。disabled な Button は hover を拾わないので span で包む */}
                <span
                  style={{
                    display: "inline-block",
                    ...(g.active ? {} : { cursor: "not-allowed" }),
                  }}
                >
                  <Button type="primary" disabled={!g.active} loading={sync.isPending}>
                    初期同期
                  </Button>
                </span>
              </Tooltip>
            </Popconfirm>
            {/* 止めるときだけ確認する(再開は確認不要)。止めるとメンバーが凍結され、
                外す作業が起票されないので、削除と同じ警告を出す */}
            <Popconfirm
              title="このグループを止める"
              description="動いている権限セットが残っていると止められない。既に配った権限は剥がれない"
              okText="止める"
              cancelText="やめる"
              disabled={!g.active}
              onConfirm={() => toggle.mutate(false)}
            >
              <Switch
                checkedChildren="稼働中"
                unCheckedChildren="止める"
                checked={g.active}
                loading={toggle.isPending}
                onChange={(v) => v && toggle.mutate(true)}
              />
            </Popconfirm>
            <Popconfirm
              title="このグループを消す"
              description="権限セットが残っていると消せない。既に配った権限は剥がれない"
              okText="消す"
              cancelText="やめる"
              onConfirm={() => remove.mutate()}
            >
              <Button danger>削除</Button>
            </Popconfirm>
          </Space>
        )}
      </Flex>

      <Descriptions size="small" column={4} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="メンバー">{g.member_count} 人</Descriptions.Item>
        <Descriptions.Item label="権限セット">{g.permission_set_count} 件</Descriptions.Item>
        <Descriptions.Item label="更新">{jst(g.updated_at)}</Descriptions.Item>
        <Descriptions.Item label="説明">{g.description || "(なし)"}</Descriptions.Item>
      </Descriptions>

      <Tabs
        items={[
          {
            key: "rule",
            label: "ルール",
            children: (
              <div>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    保存するとその場で再評価してメンバーを入れ替える。権限を実際に流すのは「初期同期」と、
                    その後の従業員の変更(異動・退職など)が引き金になったときだけ。
                  </Text>
                </Card>
                {fields.data && (
                  <GroupRuleEditor
                    value={rule}
                    fields={fields.data}
                    disabled={!canAdmin}
                    onChange={(v) => {
                      setRule(v);
                      setCount(null);
                    }}
                  />
                )}
                <Space style={{ marginTop: 12 }} wrap>
                  {/* preview は admin 限定の API。viewer に出すと押した瞬間 403
                      になるだけなので、出さない(Codex 低 1) */}
                  {canAdmin && (
                    <Button loading={preview.isPending} onClick={() => preview.mutate()}>
                      件数を確認
                    </Button>
                  )}
                  {canAdmin && (
                    <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
                      ルールを保存
                    </Button>
                  )}
                  {count !== null && <Text>いまの条件に当てはまる人: {count} 人</Text>}
                </Space>
              </div>
            ),
          },
          {
            key: "members",
            label: `メンバー(${g.member_count})`,
            children: (
              <div>
                <Table<GroupMember>
                  scroll={{ x: "max-content" }}
                  rowKey="id"
                  size="small"
                  columns={memberColumns}
                  dataSource={members.data?.items ?? []}
                  loading={members.isFetching}
                  pagination={false}
                />
                {/* 201 人目以降が黙って消えると、初期同期の起票数と画面が
                    食い違って見える(Codex 低 2) */}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {countNote(members.data?.items?.length ?? 0, members.data?.total)}
                </Text>
              </div>
            ),
          },
          {
            key: "sets",
            label: `権限セット(${g.permission_set_count})`,
            children: (
              <PermissionSetsTab groupId={id} canAdmin={canAdmin} groupActive={g.active} />
            ),
          },
          {
            key: "events",
            label: "履歴",
            children: (
              <Table<GroupEvent>
                scroll={{ x: "max-content" }}
                rowKey="id"
                size="small"
                columns={eventColumns}
                dataSource={events.data?.items ?? []}
                loading={events.isFetching}
                pagination={false}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
